import React, {useState} from "react";
import {KatButton, KatModal} from "@amzn/katal-react";
import CustomDropdown from "src/app/component/common/CustomDropdown";

interface DropdownOption {
    value: string;
    title: string;
}

interface LighthouseOffboardModalProps {
    visible: boolean;
    reasonCodes: DropdownOption[];
    /** Root cause options owned by LMAQ. Empty until they are delivered, which greys out the dropdown. */
    rootCauses?: DropdownOption[];
    onSubmit: (reasonCode: string, rootCause: string) => void;
    onClose: () => void;
}

/**
 * Offboard Decision popup for the Lighthouse flow. Reason code is mandatory; root cause is
 * shown but stays disabled until LMAQ supplies per-enforcement options (enabled once rootCauses
 * is non-empty). Offboard is gated on reason code only.
 */
const LighthouseOffboardModal: React.FC<LighthouseOffboardModalProps> = (
    {visible, reasonCodes, rootCauses = [], onSubmit, onClose}
) => {
    const [reasonCode, setReasonCode] = useState("");
    const [rootCause, setRootCause] = useState("");

    const resetForm = () => {
        setReasonCode("");
        setRootCause("");
    };

    const handleClose = () => {
        onClose();
        resetForm();
    };

    const handleSubmit = () => {
        onSubmit(reasonCode, rootCause);
        resetForm();
    };

    const isValid = !!reasonCode;

    return (
        <KatModal visible={visible} onClose={handleClose}>
            <span slot="title">Offboard Decision</span>
            <div className="odq-exempt-modal">
                <div className="odq-exempt-modal__field">
                    <CustomDropdown
                        label="Reason Code"
                        placeholder="--Select Reason Code--"
                        options={reasonCodes}
                        value={reasonCode}
                        onChange={setReasonCode}
                        required
                        testId="lighthouse-offboard-reason-select"
                    />
                </div>

                <div className="odq-exempt-modal__field">
                    <CustomDropdown
                        label="Root Cause"
                        placeholder="--Select Root Cause--"
                        options={rootCauses}
                        value={rootCause}
                        onChange={setRootCause}
                        disabled={rootCauses.length === 0}
                        testId="lighthouse-offboard-rootcause-select"
                    />
                </div>
            </div>
            <div slot="footer" className="kat-group-horizontal">
                <KatButton variant="secondary" label="Cancel" onClick={handleClose}
                           data-testid="lighthouse-offboard-cancel-btn" />
                <KatButton variant="primary" label="Offboard"
                           disabled={!isValid}
                           onClick={handleSubmit}
                           data-testid="lighthouse-offboard-submit-btn" />
            </div>
        </KatModal>
    );
};

export default LighthouseOffboardModal;
